import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  DEFAULT_ENABLED_MODULES,
  ENABLED_MODULES_STORAGE_KEY,
} from '@/constants/defaultEnabledModules';
import type { ApplicationModuleKey, EnabledModulesState } from '@/types/applicationModule';
import { EnabledModulesContext } from './enabledModulesContext';

function readStoredEnabledModules(): EnabledModulesState {
  const storedValue = window.localStorage.getItem(ENABLED_MODULES_STORAGE_KEY);
  if (storedValue === null) {
    return DEFAULT_ENABLED_MODULES;
  }
  try {
    const parsedValue = JSON.parse(storedValue) as Partial<EnabledModulesState>;
    return { ...DEFAULT_ENABLED_MODULES, ...parsedValue };
  } catch {
    return DEFAULT_ENABLED_MODULES;
  }
}

interface EnabledModulesProviderProps {
  children: ReactNode;
}

export function EnabledModulesProvider({ children }: EnabledModulesProviderProps) {
  const [enabledModules, setEnabledModules] =
    useState<EnabledModulesState>(readStoredEnabledModules);

  useEffect(
    function persistEnabledModules() {
      window.localStorage.setItem(ENABLED_MODULES_STORAGE_KEY, JSON.stringify(enabledModules));
    },
    [enabledModules]
  );

  const toggleModule = useCallback(function toggleSingleModule(moduleKey: ApplicationModuleKey) {
    setEnabledModules(function computeNextEnabledModules(currentEnabledModules) {
      return { ...currentEnabledModules, [moduleKey]: currentEnabledModules[moduleKey] === false };
    });
  }, []);

  return (
    <EnabledModulesContext.Provider value={{ enabledModules, toggleModule }}>
      {children}
    </EnabledModulesContext.Provider>
  );
}
